/**
 * PhaseSelector - Seletor de fase do pipeline
 * Mostra cor e probabilidade de cada fase
 * Versão: 1.0.0
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, ChevronsUpDown, Target } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

// Interface da fase
interface Phase {
  id: string;
  name: string;
  color: string | null;
  probability_percent: number | null;
  sort_order: number;
  is_won: boolean;
  is_lost: boolean;
}

// Props do componente
interface PhaseSelectorProps {
  pipelineId: string | null;
  value?: string | null;
  onSelect: (phaseId: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Oculta fases finais (ganho/perdido) da seleção - padrão: true */
  excludeFinalPhases?: boolean;
}

/**
 * Componente para selecionar fases do pipeline
 */
export function PhaseSelector({
  pipelineId,
  value,
  onSelect,
  placeholder = 'Selecione a fase...',
  disabled = false,
  className,
  excludeFinalPhases = true,
}: PhaseSelectorProps) {
  const { currentTenant } = useAuth();
  const [open, setOpen] = React.useState(false);

  // Busca fases do pipeline
  const { data: phases = [], isLoading } = useQuery({
    queryKey: ['pipeline-phases', pipelineId],
    queryFn: async () => {
      if (!pipelineId || !currentTenant?.id) return [];
      
      const { data, error } = await supabase
        .from('crm_pipeline_phases')
        .select('id, name, color, probability_percent, sort_order, is_won, is_lost')
        .eq('pipeline_id', pipelineId)
        .eq('tenant_id', currentTenant.id)
        .order('sort_order');
      
      if (error) throw error;
      return data as Phase[];
    },
    enabled: !!pipelineId && !!currentTenant?.id,
  });

  // Encontra fase selecionada
  const selectedPhase = phases.find(p => p.id === value);

  // Filtra fases finais (ganho/perdido) para evitar seleção manual
  // Fases finais só podem ser alteradas via botões "Marcar como Ganho/Perdido"
  const selectablePhases = excludeFinalPhases 
    ? phases.filter(p => !p.is_won && !p.is_lost)
    : phases;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled || !pipelineId}
          className={cn('w-full justify-between', className)}
        >
          <div className="flex items-center gap-2 truncate">
            {selectedPhase ? (
              <>
                <div 
                  className="h-3 w-3 rounded-full shrink-0"
                  style={{ backgroundColor: selectedPhase.color || '#6b7280' }}
                />
                <span className="truncate">{selectedPhase.name}</span>
                {selectedPhase.probability_percent !== null && (
                  <span className="text-xs text-muted-foreground">
                    ({selectedPhase.probability_percent}%)
                  </span>
                )}
              </>
            ) : (
              <>
                <Target className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">{placeholder}</span>
              </>
            )}
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <Command>
          <CommandList>
            <CommandEmpty>
              {isLoading ? 'Carregando...' : 'Nenhuma fase encontrada.'}
            </CommandEmpty>
            <CommandGroup>
              {selectablePhases.map((phase) => (
                <CommandItem
                  key={phase.id}
                  value={phase.id}
                  onSelect={() => {
                    onSelect(phase.id);
                    setOpen(false);
                  }}
                >
                  <div className="flex items-center gap-2 flex-1">
                    <div 
                      className="h-3 w-3 rounded-full shrink-0"
                      style={{ backgroundColor: phase.color || '#6b7280' }}
                    />
                    <span className="flex-1">{phase.name}</span>
                    {phase.probability_percent !== null && (
                      <span className="text-xs text-muted-foreground">
                        {phase.probability_percent}%
                      </span>
                    )}
                    {phase.is_won && (
                      <span className="text-xs text-green-600">Ganho</span>
                    )}
                    {phase.is_lost && (
                      <span className="text-xs text-red-600">Perdido</span>
                    )}
                  </div>
                  <Check
                    className={cn(
                      'ml-2 h-4 w-4',
                      value === phase.id ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
